/**
 * Coverage/dependent consistency checks for the checkout step. Cross-references the per-product
 * coverage tiers against the dependents entered on the form, surfacing mismatches the backend would
 * otherwise resolve silently (a family tier with no dependents, or a dependent no elected plan
 * covers — which e123's mapping drops). Pure and UI-free so it can drive both the warning section
 * and, later, tests. Mirrors the server-side coverage rules in enrollmentMapping.ts's
 * tierCoversDependent.
 */
import type {CoverageTier, GroupConfig, ProductCategory} from '@client/pages/public/enroll/types';
import type {
  Dependent,
  EnrollSelections,
  EnrollStep,
  EnrollTiers,
} from '@client/pages/public/enroll/enrollModel';
import {
  DEFAULT_TIER,
  getProduct,
  isProductStep,
  STEP,
  tierFor,
} from '@client/pages/public/enroll/enrollModel';

interface BaseCoverageIssue {
  id: string;
  message: string;
  editStep?: EnrollStep;
  addDependent?: boolean;
  /** Offer "Remove {name}" for a single orphaned dependent. */
  removeDependentIndex?: number;
  removeDependentName?: string;
  removeAllDependents?: boolean;
}

/**
 * Acknowledgeable issues gate submit behind a checkbox (and so must carry the checkbox copy);
 * must-resolve issues can only be fixed. Modeled as a discriminated union so an acknowledgeable
 * issue can never ship without `acknowledgeLabel` — which would be unresolvable in the UI and
 * permanently block submission.
 */
export type CoverageIssue =
  | (BaseCoverageIssue & {acknowledgeable: true; acknowledgeLabel: string})
  | (BaseCoverageIssue & {acknowledgeable: false; acknowledgeLabel?: undefined});

const SPOUSE_RELATIONSHIPS = new Set(['spouse', 'domestic-partner']);

/** Which relationships a coverage tier includes. Kept in sync with server tierCoversDependent. */
function tierCoversRelationship(tier: CoverageTier, relationship: string): boolean {
  if (tier === 'family') return true;
  if (tier === 'employeeSpouse') return SPOUSE_RELATIONSHIPS.has(relationship);
  if (tier === 'employeeChildren') return relationship === 'child';
  return false;
}

interface ElectedProduct {
  label: string;
  tier: CoverageTier;
  editStep: EnrollStep;
}

/** The step a product is edited on: its own step, its host step when inlined, else Review. */
function stepForCategory(group: GroupConfig, category: ProductCategory): EnrollStep {
  const product = getProduct(group, category);
  if (product?.inlineWith === 'protection') return STEP.Protection;
  if (product?.inlineWith && isProductStep(product.inlineWith)) return product.inlineWith;
  if (isProductStep(category)) return category;
  return STEP.Review;
}

function dependentName(dep: Dependent, index: number): string {
  const name = `${dep.firstName ?? ''} ${dep.lastName ?? ''}`.trim();
  return name || `Dependent ${index + 1}`;
}

const RELATIONSHIP_LABEL: Record<string, string> = {
  spouse: 'spouse',
  'domestic-partner': 'domestic partner',
  child: 'child',
  other: 'other',
};

const RELATIONSHIP_COVERAGE_NOUN: Record<string, string> = {
  spouse: 'spouse',
  'domestic-partner': 'spouse',
  child: 'child',
};

/**
 * Cross-check elected tiers against entered dependents. Returns one issue per distinct problem;
 * an empty array means the elections and dependents are consistent.
 */
export function analyzeCoverage(
  group: GroupConfig,
  tiers: EnrollTiers,
  selections: EnrollSelections,
  dependents: Dependent[],
): CoverageIssue[] {
  // Elected = a chosen plan (not declined). Employee-only products count at the Employee tier.
  const elected: ElectedProduct[] = [];
  for (const product of group.products) {
    const selection = selections[product.category];
    if (!selection?.planId || selection.declined) continue;
    elected.push({
      label: product.label,
      tier: product.tierSelectable === false ? DEFAULT_TIER : tierFor(tiers, product.category),
      editStep: stepForCategory(group, product.category),
    });
  }

  const deps = dependents ?? [];
  const issues: CoverageIssue[] = [];

  // With nothing elected, tier/orphan checks are moot — the only concern is stranded dependents.
  if (elected.length === 0) {
    if (deps.length > 0) {
      issues.push({
        id: 'dependents-no-coverage',
        message:
          "You've added dependents but aren't enrolling in any coverage, so no one will be enrolled.",
        acknowledgeable: false,
        editStep: STEP.Review,
        removeAllDependents: true,
      });
    }
    return issues;
  }

  const hasSpouse = deps.some((d) => d.relationship && SPOUSE_RELATIONSHIPS.has(d.relationship));
  const hasChild = deps.some((d) => d.relationship === 'child');

  const editStepFor = (products: ElectedProduct[]): EnrollStep =>
    products.length === 1 ? products[0].editStep : STEP.Review;
  const labelList = (products: ElectedProduct[]): string => products.map((p) => p.label).join(', ');

  // Issue 1: a tier requires a dependent that isn't present. Hard stop — the tier can't be
  // submitted without the matching dependent (add one or switch tiers).
  const spouseTierProducts = elected.filter((p) => p.tier === 'employeeSpouse');
  if (spouseTierProducts.length > 0 && !hasSpouse) {
    issues.push({
      id: 'missing-spouse',
      message: `You selected spouse coverage (${labelList(spouseTierProducts)}) but haven't added a spouse or domestic partner.`,
      acknowledgeable: false,
      editStep: editStepFor(spouseTierProducts),
      addDependent: true,
    });
  }

  const childTierProducts = elected.filter((p) => p.tier === 'employeeChildren');
  if (childTierProducts.length > 0 && !hasChild) {
    issues.push({
      id: 'missing-child',
      message: `You selected child coverage (${labelList(childTierProducts)}) but haven't added a child.`,
      acknowledgeable: false,
      editStep: editStepFor(childTierProducts),
      addDependent: true,
    });
  }

  // Family requires both a spouse and a child. Hard stop the incomplete cases: no dependents,
  // only spouse(s), or only children — the member must add the missing dependent or switch to the
  // tier that matches who they entered.
  const familyTierProducts = elected.filter((p) => p.tier === 'family');
  if (familyTierProducts.length > 0) {
    const familyStep = editStepFor(familyTierProducts);
    const familyLabels = labelList(familyTierProducts);
    const allSpouses = deps.every((d) => d.relationship && SPOUSE_RELATIONSHIPS.has(d.relationship));
    const allChildren = deps.every((d) => d.relationship === 'child');
    if (deps.length === 0) {
      issues.push({
        id: 'family-no-dependents',
        message: `You selected family coverage (${familyLabels}) but haven't added any dependents.`,
        acknowledgeable: false,
        editStep: familyStep,
        addDependent: true,
      });
    } else if (allSpouses) {
      issues.push({
        id: 'family-overpay-no-child',
        message: `You selected family coverage (${familyLabels}) but only added a spouse. Add a child, or switch to Employee + Spouse coverage.`,
        acknowledgeable: false,
        editStep: familyStep,
        addDependent: true,
      });
    } else if (allChildren) {
      issues.push({
        id: 'family-overpay-no-spouse',
        message: `You selected family coverage (${familyLabels}) but only added children. Add a spouse, or switch to Employee + Child(ren) coverage.`,
        acknowledgeable: false,
        editStep: familyStep,
        addDependent: true,
      });
    }
  }

  // Issue 2: a dependent no elected plan covers (the backend drops them). Must resolve — no checkbox.
  deps.forEach((dep, index) => {
    if (!dep.relationship) return; // incomplete row — caught by field validation, not here
    if (elected.some((p) => tierCoversRelationship(p.tier, dep.relationship))) return;
    const name = dependentName(dep, index);
    const relLabel = RELATIONSHIP_LABEL[dep.relationship] ?? dep.relationship;
    const noun = RELATIONSHIP_COVERAGE_NOUN[dep.relationship];
    issues.push({
      id: `orphan-${index}`,
      message: noun
        ? `${name} (${relLabel}) won't be enrolled — none of your selected plans include ${noun} coverage.`
        : `${name} (${relLabel}) won't be enrolled — none of your selected plans cover this dependent.`,
      acknowledgeable: false,
      editStep: STEP.Review,
      removeDependentIndex: index,
      removeDependentName: name,
    });
  });

  return issues;
}
